import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, List, FolderOpen, MessageSquare, HelpCircle, Archive, Keyboard, ArrowLeft } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'

type TutorialSection = 'overview' | 'entities' | 'threads' | 'questions' | 'archive' | 'shortcuts'

interface TutorialMenuItem {
  id: TutorialSection
  icon: React.ElementType
  label: string
}

export function TutorialsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState<TutorialSection>('overview')

  const menuItems: TutorialMenuItem[] = [
    { id: 'overview', icon: BookOpen, label: t('tutorialOverview') },
    { id: 'entities', icon: FolderOpen, label: t('tutorialEntities') },
    { id: 'threads', icon: MessageSquare, label: t('tutorialThreads') },
    { id: 'questions', icon: HelpCircle, label: t('tutorialQuestions') },
    { id: 'archive', icon: Archive, label: t('tutorialArchive') },
    { id: 'shortcuts', icon: Keyboard, label: t('tutorialShortcuts') },
  ]

  return (
    <div className="h-full flex">
      {/* Left Navigation Sidebar */}
      <div className="w-64 border-r bg-background">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-1">
            <button
              aria-label={t('back')}
              className="inline-flex items-center justify-center h-9 w-9 rounded hover:bg-accent/40"
              onClick={() => { if (window.history.length > 1) { navigate(-1) } else { navigate('/', { replace: true }) } }}
            >
              <ArrowLeft size={18} />
            </button>
            <h2 className="text-lg font-semibold">{t('tutorials')}</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{t('tutorialsDescription')}</p>
        </div>
        <ScrollArea className="h-[calc(100vh-120px)]">
          <nav className="p-2 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors",
                    activeSection === item.id
                      ? "bg-accent text-accent-foreground font-medium"
                      : "hover:bg-accent/50"
                  )}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              )
            })}
          </nav>
        </ScrollArea>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 overflow-auto">
        <ScrollArea className="h-full">
          <div className="max-w-4xl mx-auto p-8 space-y-8">
            {activeSection === 'overview' && <OverviewSection />}
            {activeSection === 'entities' && <EntitiesSection />}
            {activeSection === 'threads' && <ThreadsSection />}
            {activeSection === 'questions' && <QuestionsSection />}
            {activeSection === 'archive' && <ArchiveSection />}
            {activeSection === 'shortcuts' && <ShortcutsSection />}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}

function OverviewSection() {
  const { t } = useTranslation()
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('tutorialOverview')}</h1>
        <p className="text-muted-foreground mt-2">{t('tutorialOverviewContent')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialApplicationStructureTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialApplicationStructureIntro')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>{t('tutorialApplicationStructureEntities')}</li>
            <li>{t('tutorialApplicationStructureThreads')}</li>
            <li>{t('tutorialApplicationStructureQA')}</li>
            <li>{t('tutorialApplicationStructureArchive')}</li>
            {/* Email Extractor removed */}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialGettingStartedTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal list-inside space-y-3 ml-4">
            <li>{t('tutorialGettingStartedStep1')}</li>
            <li>{t('tutorialGettingStartedStep2')}</li>
            <li>{t('tutorialGettingStartedStep3')}</li>
            <li>{t('tutorialGettingStartedStep4')}</li>
            <li>{t('tutorialGettingStartedStep5')}</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  )
}

function EntitiesSection() {
  const { t } = useTranslation()
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('tutorialEntities')}</h1>
        <p className="text-muted-foreground mt-2">{t('tutorialEntitiesContent')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialEntitiesCreatingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialEntitiesCreatingIntro')}</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>{t('tutorialEntitiesCreatingStep1')}</li>
            <li>{t('tutorialEntitiesCreatingStep2')}</li>
            <li>{t('tutorialEntitiesCreatingStep3')}</li>
            <li>{t('tutorialEntitiesCreatingStep4')}</li>
          </ol>
          <p className="text-sm text-muted-foreground mt-4">
            {t('tutorialEntitiesTip')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialEntitiesManagingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialEntitiesManagingIntro')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>{t('tutorialEntitiesManagingEdit')}</li>
            <li>{t('tutorialEntitiesManagingArchive')}</li>
            <li>{t('tutorialEntitiesManagingDelete')}</li>
            <li>{t('tutorialEntitiesManagingExportImport')}</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialEntitiesListViewTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialEntitiesListViewIntro')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>{t('tutorialEntitiesListViewItem1')}</li>
            <li>{t('tutorialEntitiesListViewItem2')}</li>
            <li>{t('tutorialEntitiesListViewItem3')}</li>
            <li>{t('tutorialEntitiesListViewItem4')}</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

function ThreadsSection() {
  const { t } = useTranslation()
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('tutorialThreads')}</h1>
        <p className="text-muted-foreground mt-2">{t('tutorialThreadsContent')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialThreadsCreatingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialThreadsCreatingIntro')}</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>{t('tutorialThreadsCreatingStep1')}</li>
            <li>{t('tutorialThreadsCreatingStep2')}</li>
            <li>{t('tutorialThreadsCreatingStep3')}</li>
            <li>{t('tutorialThreadsCreatingStep4')}</li>
            <li>{t('tutorialThreadsCreatingStep5')}</li>
          </ol>
          <p className="text-sm text-muted-foreground mt-4">
            {t('tutorialThreadsTip')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialThreadsViewsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialThreadsViewsIntro')}</p>
          <ul className="list-disc list-inside space-y-3 ml-4">
            <li>
              <strong>{t('tutorialThreadsViewsCardTitle')}</strong>
              <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                <li>{t('tutorialThreadsViewsCardItem1')}</li>
                <li>{t('tutorialThreadsViewsCardItem2')}</li>
                <li>{t('tutorialThreadsViewsCardItem3')}</li>
              </ul>
            </li>
            <li>
              <strong>{t('tutorialThreadsViewsCompactTitle')}</strong>
              <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                <li>{t('tutorialThreadsViewsCompactItem1')}</li>
                <li>{t('tutorialThreadsViewsCompactItem2')}</li>
                <li>{t('tutorialThreadsViewsCompactItem3')}</li>
              </ul>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialThreadsManagementTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialThreadsManagementIntro')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>{t('tutorialThreadsManagementItem1')}</li>
            <li>{t('tutorialThreadsManagementItem2')}</li>
            <li>{t('tutorialThreadsManagementItem3')}</li>
            <li>{t('tutorialThreadsManagementItem4')}</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

function QuestionsSection() {
  const { t } = useTranslation()
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('tutorialQuestions')}</h1>
        <p className="text-muted-foreground mt-2">{t('tutorialQuestionsContent')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialQuestionsTypesTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialQuestionsTypesIntro')}</p>
          <ul className="list-disc list-inside space-y-3 ml-4">
            <li>
              <strong>{t('tutorialQuestionsTypesRootTitle')}</strong>
              <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                <li>{t('tutorialQuestionsTypesRootItem1')}</li>
                <li>{t('tutorialQuestionsTypesRootItem2')}</li>
              </ul>
            </li>
            <li>
              <strong>{t('tutorialQuestionsTypesFollowupTitle')}</strong>
              <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                <li>{t('tutorialQuestionsTypesFollowupItem1')}</li>
                <li>{t('tutorialQuestionsTypesFollowupItem2')}</li>
                <li>{t('tutorialQuestionsTypesFollowupItem3')}</li>
              </ul>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialAnswerTypesTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialAnswerTypesIntro')}</p>
          <div className="space-y-4 mt-4">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <strong>{t('conclusive')}</strong>
              </div>
              <p className="text-sm text-muted-foreground ml-5 mt-1">
                {t('tutorialConclusiveDesc')}
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <strong>{t('inconclusive')}</strong>
              </div>
              <p className="text-sm text-muted-foreground ml-5 mt-1">
                {t('tutorialInconclusiveDesc')}
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            {t('tutorialAnswersTip')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('consequences')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialConsequencesIntro')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>{t('tutorialConsequencesItem1')}</li>
            <li>{t('tutorialConsequencesItem2')}</li>
            <li>{t('tutorialConsequencesItem3')}</li>
          </ul>
          <p className="text-sm text-muted-foreground mt-4">
            {t('tutorialConsequencesHowTo')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialRichTextTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialRichTextIntro')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>{t('tutorialRichTextItem1')}</li>
            <li>{t('tutorialRichTextItem2')}</li>
            <li>{t('tutorialRichTextItem3')}</li>
            <li>{t('tutorialRichTextItem4')}</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

function ArchiveSection() {
  const { t } = useTranslation()
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('tutorialArchive')}</h1>
        <p className="text-muted-foreground mt-2">{t('tutorialArchiveContent')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialArchiveArchivingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialArchiveArchivingIntro')}</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>{t('tutorialArchiveArchivingStep1')}</li>
            <li>{t('tutorialArchiveArchivingStep2')}</li>
            <li>{t('tutorialArchiveArchivingStep3')}</li>
          </ol>
          <p className="text-sm text-muted-foreground mt-4">
            {t('tutorialArchiveArchivingNote')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialArchiveAccessingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialArchiveAccessingIntro')}</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>{t('tutorialArchiveAccessingStep1')}</li>
            <li>{t('tutorialArchiveAccessingStep2')}</li>
            <li>{t('tutorialArchiveAccessingStep3')}</li>
          </ol>
          <p className="text-sm bg-muted p-3 rounded-md mt-4">
            {t('tutorialArchiveNote')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialArchiveRestoringTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialArchiveRestoringIntro')}</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>{t('tutorialArchiveRestoringStep1')}</li>
            <li>{t('tutorialArchiveRestoringStep2')}</li>
            <li>{t('tutorialArchiveRestoringStep3')}</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  )
}

function ShortcutsSection() {
  const { t } = useTranslation()
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('tutorialShortcuts')}</h1>
        <p className="text-muted-foreground mt-2">{t('tutorialShortcutsContent')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialShortcutsEnablingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialShortcutsEnablingIntro')}</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>{t('tutorialShortcutsEnablingStep1')}</li>
            <li>{t('tutorialShortcutsEnablingStep2')}</li>
            <li>{t('tutorialShortcutsEnablingStep3')}</li>
          </ol>
          <p className="text-sm text-muted-foreground mt-4">
            {t('tutorialShortcutsEnablingNote')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialShortcutsGlobalTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('toggleSidebar')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">B</kbd>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialShortcutsEntitiesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutCreateEntity')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">A</kbd>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutImportEntity')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">Z</kbd>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialShortcutsEntityDetailTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutCreateThread')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">A</kbd>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutImportThread')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">Z</kbd>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutSaveAll')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">Ctrl+S</kbd>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutCancelAll')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">Ctrl+D</kbd>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('tutorialShortcutsThreadPageTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutCreateConclusiveAnswer')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">S</kbd>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutCreateInconclusiveAnswer')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">D</kbd>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span>{t('shortcutCreateFollowupQuestion')}</span>
              <kbd className="bg-muted px-3 py-1 rounded text-sm font-mono">F</kbd>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('dataManagement')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>{t('tutorialDataManagementIntro')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>{t('tutorialDataResetDemo')}</li>
            <li>{t('tutorialDataDeleteAll')}</li>
            <li>{t('tutorialDataExport')}</li>
            <li>{t('tutorialDataImport')}</li>
          </ul>
          <p className="text-sm bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 rounded-md mt-4">
            {t('tutorialDataWarning')}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
